import { memo, useEffect, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Popover, Transition } from '@headlessui/react';
import RecordingRow from './RecordingRow';
import DuplicateRecordingRow from './DuplicateRecordingRow';
import DuplicateRecordingModal from './DuplicateRecordingModal';
import Icon from '@/components/atoms/Icon';
import { core } from '@/stores/core';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRecordingDuplicatesStore } from '@/stores/duplicateRecordingStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';
import useAnalytics from '@/hooks/useAnalytics';
import { showErrorToast } from '@/libs/toast/toast';

interface RecordingItem {
  id: string;
  title: string;
  label?: string;
  isOriginal?: boolean;
}

function RecordingDuplicateDropdown() {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const { clipData } = useClipsContext();
  const recordings = useRecordingDuplicatesStore(state => state.recordings);
  const isLoading = useRecordingDuplicatesStore(state => state.isLoading);
  const loadRecordings = useRecordingDuplicatesStore(state => state.loadRecordings);
  const removeRecording = useRecordingDuplicatesStore(state => state.removeRecording);
  const [, setSearchParams] = useSearchParams();

  const { onCreateDuplicate } = useCreateClip({ clip: clipData });
  const { trackRecordingDuplicate } = useAnalytics();

  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const recordingTitle = coreStore.content?.title || 'Original';
  const activeClipId = clipData.id;
  const originalClipId = coreStore.content?.full_length_asset_id ?? activeClipId;
  const broadcastId = coreStore.content?.id ?? '';

  const duplicates = recordings[broadcastId] ?? [];
  const activeDuplicate = duplicates.find(d => d.id === activeClipId);
  const activeRecordingTitle = activeClipId === originalClipId ? 'Original' : (activeDuplicate?.title ?? 'Original');

  const existingTitles = new Set(duplicates.map(d => d.title));
  const baseCopyName = `Copy of ${activeRecordingTitle}`;
  let defaultDuplicateName = baseCopyName;
  if (existingTitles.has(baseCopyName)) {
    let counter = 2;
    while (existingTitles.has(`Copy ${counter} of ${activeRecordingTitle}`)) {
      counter++;
    }
    defaultDuplicateName = `Copy ${counter} of ${activeRecordingTitle}`;
  }

  const handleConfirmDuplicate = async (name: string) => {
    if (isCreating) {
      return;
    }
    setIsCreating(true);
    trackRecordingDuplicate({ action: 'Create', state: 'Started', sourceRecordingId: originalClipId });
    try {
      await onCreateDuplicate(name);
      trackRecordingDuplicate({ action: 'Create', state: 'Success', sourceRecordingId: originalClipId });
      setIsModalOpen(false);
    } catch (err) {
      trackRecordingDuplicate({
        action: 'Create',
        state: 'Failed',
        sourceRecordingId: originalClipId,
        failureReason: err instanceof Error ? err.message : String(err)
      });
      showErrorToast('Failed to create duplicate. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (!featureFlags[FeatureFlagKeys.Use_CL_Clip_Duplication] || !broadcastId) {
      return;
    }
    loadRecordings(broadcastId);
  }, [broadcastId, featureFlags, loadRecordings]);

  const recordingList: RecordingItem[] = [
    { id: originalClipId, title: recordingTitle, label: 'Original', isOriginal: true },
    ...duplicates
  ];

  const isActive = (id: string) => id === activeClipId;

  const handleDuplicateDeleted = (recordingId: string) => {
    removeRecording(broadcastId, recordingId);
    // If the deleted duplicate is the one currently open, fall back to the original recording.
    if (recordingId === activeClipId) {
      setSearchParams(
        prev => {
          prev.delete('activeDuplicateId');
          return prev;
        },
        { replace: true }
      );
    }
  };

  const handleSelectRecording = (id: string) => {
    const isOriginal = recordingList.find(recording => recording.id === id && recording.isOriginal);
    // Only a switch to a duplicate is a "duplicate opened"; returning to the original isn't.
    if (!isOriginal && id !== activeClipId) {
      trackRecordingDuplicate({ action: 'Open', recordingId: id, sourceRecordingId: originalClipId });
    }
    setSearchParams(
      prev => {
        if (isOriginal) {
          prev.delete('activeDuplicateId');
        } else {
          prev.set('activeDuplicateId', id);
        }
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <Popover className="relative z-50">
      {({ open }) => (
        <>
          <Popover.Button
            className="flex items-center rounded p-1 text-slate-500 hover:bg-slate-200 focus:outline-none"
            aria-label="Duplicate recordings"
          >
            <Icon name={open ? 'IconChevronUp' : 'IconChevronDown'} size={14} />
          </Popover.Button>

          <Transition
            enter="transition duration-150 ease-out"
            enterFrom="transform scale-[0.98] opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-100 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-[0.98] opacity-0"
          >
            <Popover.Panel className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
              {/* Recordings list. The original recording is always shown and carries no row actions;
                  duplicates render through DuplicateRecordingRow, which wires their download/delete. */}
              <div className="max-h-[50vh] overflow-auto">
                <RecordingRow
                  id={originalClipId}
                  title={recordingTitle}
                  label="Original"
                  isOriginal
                  showActionButton={false}
                  isActive={isActive(originalClipId)}
                  onSelect={() => handleSelectRecording(originalClipId)}
                />
                {isLoading ? (
                  <div className="p-2 text-sm text-slate-400">Loading...</div>
                ) : (
                  duplicates.map(recording => (
                    <DuplicateRecordingRow
                      key={recording.id}
                      recording={recording}
                      isActive={isActive(recording.id)}
                      onSelect={() => handleSelectRecording(recording.id)}
                      onDeleted={handleDuplicateDeleted}
                    />
                  ))
                )}
              </div>

              <hr className="my-1 border-slate-100" />

              <button
                disabled={isCreating}
                onClick={() => setIsModalOpen(true)}
                className="flex w-full items-center gap-2 rounded p-2 text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                <Icon name="IconPlus" size={14} />
                <span>{isCreating ? 'Creating...' : 'Create duplicate'}</span>
              </button>
            </Popover.Panel>
          </Transition>

          {isModalOpen && (
            <DuplicateRecordingModal
              isOpen={isModalOpen}
              defaultName={defaultDuplicateName}
              isCreating={isCreating}
              onClose={() => setIsModalOpen(false)}
              onConfirm={handleConfirmDuplicate}
            />
          )}
        </>
      )}
    </Popover>
  );
}

export default memo(RecordingDuplicateDropdown);
